import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Search, Zap, TrendingUp, ChevronRight, Car } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PowerCatalogPage = () => {
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [engines, setEngines] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [stats, setStats] = useState({ total_vehicles: 0, total_brands: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/catalog/brands`).then(r => setBrands(r.data.brands || [])).catch(() => {});
    axios.get(`${API}/catalog/stats`).then(r => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedBrand) { setModels([]); setEngines([]); setSelectedModel(''); return; }
    axios.get(`${API}/catalog/models?brand=${encodeURIComponent(selectedBrand)}`).then(r => setModels(r.data.models || [])).catch(() => {});
    setSelectedModel(''); setEngines([]);
  }, [selectedBrand]);

  useEffect(() => {
    if (!selectedBrand || !selectedModel) { setEngines([]); return; }
    setLoading(true);
    axios.get(`${API}/catalog/engines?brand=${encodeURIComponent(selectedBrand)}&model=${encodeURIComponent(selectedModel)}`)
      .then(r => setEngines(r.data.engines || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedBrand, selectedModel]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/catalog/search?q=${encodeURIComponent(searchQuery)}&limit=20`);
      setSearchResults(res.data.vehicles || []);
    } catch {}
    setLoading(false);
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto" data-testid="power-catalog-page">
        <div className="text-center mb-8">
          <h1 className="font-heading text-4xl font-bold text-white mb-2">CATALOGO DE POTENCIA</h1>
          <p className="text-zinc-400">
            <span className="text-primary font-bold">{stats.total_vehicles?.toLocaleString()}</span> vehiculos de{' '}
            <span className="text-primary font-bold">{stats.total_brands}</span> marcas con datos de Stage 1, Stage 2 y mas
          </p>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2 mb-6">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Buscar vehiculo... (ej: Golf GTI, BMW 320d, Peugeot 308)"
              className="w-full bg-surface border border-white/10 rounded-md pl-10 pr-4 py-3 text-white placeholder:text-zinc-600 focus:border-primary/50 focus:outline-none"
              data-testid="catalog-search" />
          </div>
          <Button type="submit" className="bg-primary px-6"><Search className="w-4 h-4" /></Button>
        </form>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="mb-6 space-y-2">
            <h3 className="text-zinc-400 text-sm">Resultados de busqueda:</h3>
            {searchResults.map((v, i) => <VehicleCard key={i} vehicle={v} />)}
          </div>
        )}

        {/* Brand/Model Selector */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="text-zinc-500 text-xs uppercase tracking-wider mb-1 block">Marca</label>
            <select value={selectedBrand} onChange={e => setSelectedBrand(e.target.value)}
              className="w-full bg-surface border border-white/10 rounded-md px-4 py-3 text-white" data-testid="catalog-brand">
              <option value="">Seleccionar marca...</option>
              {brands.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className="text-zinc-500 text-xs uppercase tracking-wider mb-1 block">Modelo</label>
            <select value={selectedModel} onChange={e => setSelectedModel(e.target.value)} disabled={!selectedBrand}
              className="w-full bg-surface border border-white/10 rounded-md px-4 py-3 text-white disabled:opacity-40" data-testid="catalog-model">
              <option value="">Seleccionar modelo...</option>
              {models.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>

        {/* Engine Results */}
        {loading && <div className="text-center py-8 text-zinc-500">Cargando...</div>}

        {engines.length > 0 && (
          <div className="space-y-3">
            <h2 className="font-heading text-white text-lg">{selectedBrand} {selectedModel}</h2>
            {engines.map((eng, i) => <VehicleCard key={i} vehicle={{ brand: selectedBrand, model: selectedModel, ...eng }} />)}
          </div>
        )}

        {/* Popular brands grid */}
        {!selectedBrand && searchResults.length === 0 && (
          <div>
            <h3 className="text-zinc-400 text-sm uppercase tracking-wider mb-3">Marcas populares</h3>
            <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 gap-2">
              {['Audi', 'BMW', 'Mercedes', 'Volkswagen', 'Seat', 'Skoda', 'Ford', 'Peugeot', 'Citroën', 'Renault', 'Toyota', 'Hyundai', 'Kia', 'Nissan',
                'Opel', 'Fiat', 'Alfa Romeo', 'Volvo', 'Jaguar', 'Land Rover', 'Porsche'].map(b => (
                <button key={b} onClick={() => setSelectedBrand(b)}
                  className="bg-surface border border-white/5 rounded-md p-3 text-center hover:border-primary/30 hover:bg-primary/5 transition-colors">
                  <Car className="w-4 h-4 text-zinc-600 mx-auto mb-1" />
                  <span className="text-white text-xs">{b}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

const VehicleCard = ({ vehicle }) => {
  const stages = vehicle.stages || [];
  return (
    <div className="bg-surface border border-white/10 rounded-md p-4 hover:border-primary/20 transition-colors" data-testid="vehicle-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-white font-medium">{vehicle.brand} {vehicle.model}</h3>
          <p className="text-zinc-500 text-sm">{vehicle.year_range} {vehicle.engine}</p>
        </div>
        {vehicle.power_ori > 0 && <span className="text-zinc-400 font-mono text-sm">{vehicle.power_ori}CV</span>}
      </div>
      {stages.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {stages.map((s, i) => (
            <div key={i} className="bg-black/30 rounded px-3 py-2">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-3 h-3 text-primary" />
                <span className="text-primary text-xs font-bold">STAGE {s.stage}</span>
              </div>
              <div className="grid grid-cols-2 gap-x-4 text-xs">
                <div>
                  <span className="text-zinc-500">Potencia</span>
                  <p className="text-white font-mono">{s.potencia_original} <ChevronRight className="w-3 h-3 inline text-primary" /> <span className="text-primary font-bold">{s.potencia_repro}CV</span></p>
                  <p className="text-green-400 text-[10px]">+{s.potencia_ganancia}CV</p>
                </div>
                <div>
                  <span className="text-zinc-500">Par</span>
                  <p className="text-white font-mono">{s.par_original} <ChevronRight className="w-3 h-3 inline text-primary" /> <span className="text-primary font-bold">{s.par_repro}Nm</span></p>
                  <p className="text-green-400 text-[10px]">+{s.par_ganancia}Nm</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PowerCatalogPage;
